import React, { useState } from 'react';
import { X, Pencil, Minus, Plus } from 'lucide-react';
import { ptsFor } from '../utils/points';

const EditChoreModal = ({ chore, existingFolders, onClose, onSave }) => {
  const [title, setTitle] = useState(chore.title);
  const [folder, setFolder] = useState(chore.folder || 'General');
  const [isNewFolder, setIsNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [minutes, setMinutes] = useState(chore.defaultMinutes || 10);
  const [difficulty, setDifficulty] = useState(chore.defaultDifficulty || 2);

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalFolder = isNewFolder ? newFolderName : folder;
    onSave(chore.id, { title, folder: finalFolder, defaultMinutes: parseInt(minutes), defaultDifficulty: parseInt(difficulty) });
  };

  return (
    <div className="fixed inset-0 bg-ink-overlay/70 backdrop-blur-sm z-50 flex items-end justify-center" onClick={onClose}>
      <div
        className="bg-ink-card2 w-full max-w-lg rounded-t-3xl p-6 shadow-2xl border-t border-white/10 animate-in slide-in-from-bottom-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-serif text-ink-fg flex items-center gap-2">
            <Pencil size={18} className="text-mint" /> Edit Chore
          </h2>
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-1.5 rounded-lg bg-mint/15 text-mint text-[11px] font-mono font-semibold">{ptsFor(minutes, difficulty)} PTS</span>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-ink-card3 text-ink-fg/40 hover:text-ink-fg/70 transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-1">Chore Name</label>
            <input
              className="w-full bg-ink-card3 border border-white/10 text-ink-fg text-sm rounded-xl focus:ring-2 focus:ring-mint focus:border-transparent p-3 outline-none placeholder:text-ink-fg/30 font-medium"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          <div>
            <label className="block text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-1">Folder / Category</label>
            <select
              className="w-full bg-ink-card3 border border-white/10 text-ink-fg text-sm rounded-xl focus:ring-2 focus:ring-mint focus:border-transparent p-3 outline-none mb-2"
              value={isNewFolder ? "__new__" : folder}
              onChange={(e) => {
                if (e.target.value === "__new__") {
                  setIsNewFolder(true);
                } else {
                  setIsNewFolder(false);
                  setFolder(e.target.value);
                }
              }}
            >
              {existingFolders.map(f => <option key={f} value={f}>{f}</option>)}
              <option value="__new__">+ New Folder...</option>
            </select>
            {isNewFolder && (
              <input
                className="w-full bg-mint/10 border border-mint/30 text-ink-fg text-sm rounded-xl focus:ring-2 focus:ring-mint focus:border-transparent p-3 outline-none placeholder:text-ink-fg/30 animate-in fade-in slide-in-from-top-1"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Enter new folder name..."
                autoFocus
                required
              />
            )}
          </div>
          <div className="bg-ink-card3 p-4 rounded-2xl border border-white/10 space-y-5">
            <div>
              <label className="block text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-2">Default Time</label>
              <div className="flex items-center justify-between">
                <div className="flex items-baseline gap-1.5">
                  <span className="text-3xl font-serif text-ink-fg">{minutes}</span>
                  <span className="text-xs font-mono text-ink-fg/40 uppercase">min</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setMinutes(m => Math.max(1, parseInt(m || 0) - 5))}
                    className="w-9 h-9 rounded-xl bg-ink-card2 border border-white/10 text-ink-fg/70 flex items-center justify-center hover:bg-ink-card hover:text-mint transition-colors active:scale-90"
                  >
                    <Minus size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setMinutes(m => parseInt(m || 0) + 5)}
                    className="w-9 h-9 rounded-xl bg-ink-card2 border border-white/10 text-ink-fg/70 flex items-center justify-center hover:bg-ink-card hover:text-mint transition-colors active:scale-90"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-mono font-medium text-ink-fg/50 uppercase tracking-wider mb-2">Default Difficulty</label>
              <div className="flex gap-1.5">
                {[1, 2, 3, 4, 5].map(n => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setDifficulty(n)}
                    className={`flex-1 h-9 rounded-xl text-sm font-bold transition-all ${n <= difficulty
                      ? 'bg-mint text-ink-shell'
                      : 'bg-ink-card2 text-ink-fg/35 border border-white/10 hover:border-mint/25'
                      }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <button
            type="submit"
            className="w-full bg-mint hover:brightness-95 text-ink-shell p-3 rounded-xl shadow-md transition-all font-bold mt-2"
          >
            Save Changes
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditChoreModal;
